import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Handshake, Users2, TrendingUp, CalendarHeart, ArrowRight,
  Factory, Building2, Store, Cpu, HeartPulse, GraduationCap, Landmark, Boxes,
  UserPlus, Search, Link2, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const BENEFITS = [
  { icon: Handshake, title: "Connect", desc: "Discover trusted members and professionals across the community." },
  { icon: Users2, title: "Collaborate", desc: "Find business partners, suppliers and vendors you can rely on." },
  { icon: TrendingUp, title: "Grow", desc: "Generate referrals and explore real business opportunities." },
  { icon: CalendarHeart, title: "Engage", desc: "Participate in events and community-building initiatives." },
];

const INDUSTRIES = [
  { name: "Manufacturing & Engineering", icon: Factory },
  { name: "Construction & Real Estate", icon: Building2 },
  { name: "Retail & Trading", icon: Store },
  { name: "IT & Technology", icon: Cpu },
  { name: "Healthcare", icon: HeartPulse },
  { name: "Education & Training", icon: GraduationCap },
  { name: "Finance & Professional Services", icon: Landmark },
  { name: "Other Industries", icon: Boxes },
];

const STEPS = [
  { icon: UserPlus, title: "Create your profile", desc: "Set up your professional identity in minutes." },
  { icon: Search, title: "Discover members & businesses", desc: "Search and filter across the directory." },
  { icon: Link2, title: "Connect & explore", desc: "Send requests and respond to opportunities." },
  { icon: Sparkles, title: "Grow together", desc: "Build referrals and lasting partnerships." },
];

export default function Landing() {
  const { user } = useAuth();
  const authed = user && typeof user === "object";
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#FEF7E6]">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#2563EB]/10 blur-3xl" />
        <div className="absolute -left-20 bottom-0 h-64 w-64 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="container-nx relative grid grid-cols-1 items-center gap-12 py-16 sm:py-20 lg:grid-cols-2 lg:py-28">
          <div className="animate-fade-up">
            <div className="label-eyebrow">Vishwakarma Nexus</div>
            <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight text-[#0B192C] font-head sm:text-5xl lg:text-6xl">
              Stronger Together.<br />
              <span className="text-[#2563EB]">A Brighter Tomorrow.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Discover trusted connections, explore business opportunities and grow alongside the
              Vishwakarma business community.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to={authed ? "/dashboard" : "/register"} className="btn-gold" data-testid="hero-join-btn">
                Join the Community <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/businesses" className="btn-outline-navy" data-testid="hero-explore-btn">
                Explore Business Directory
              </Link>
            </div>
          </div>
          <div className="relative animate-fade-up">
            <div className="overflow-hidden rounded-2xl shadow-xl ring-1 ring-slate-200">
              <img src={HERO_IMG} alt="Vishwakarma entrepreneurs networking" className="h-[300px] w-full object-cover sm:h-[440px]" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-nx">
        <div className="container-nx">
          <div className="text-center max-w-2xl mx-auto">
            <div className="label-eyebrow mb-3">Why join</div>
            <h2 className="heading-2">Everything you need to grow your business</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b) => (
              <div key={b.title} className="card-nx card-nx-hover p-6" data-testid={`benefit-${b.title.toLowerCase()}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#2563EB]/10">
                  <b.icon className="h-6 w-6 text-[#2563EB]" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-[#0B192C] font-head">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section-nx bg-[#F5F3EF]">
        <div className="container-nx">
          <div className="text-center max-w-2xl mx-auto">
            <div className="label-eyebrow mb-3">Industries</div>
            <h2 className="heading-2">A community across every sector</h2>
            <p className="mt-4 text-base text-slate-500">From the workshop floor to the boardroom — find your people.</p>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {INDUSTRIES.map((i) => (
              <Link
                key={i.name}
                to={`/businesses?industry=${encodeURIComponent(i.name)}`}
                className="card-nx card-nx-hover group flex flex-col items-center gap-3 p-6 text-center"
                data-testid={`industry-${i.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#D4AF37]/15 transition group-hover:bg-[#D4AF37]/25">
                  <i.icon className="h-7 w-7 text-[#B8860B]" />
                </div>
                <span className="text-sm font-semibold text-[#0B192C]">{i.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section-nx">
        <div className="container-nx">
          <div className="text-center max-w-2xl mx-auto">
            <div className="label-eyebrow mb-3">How it works</div>
            <h2 className="heading-2">Get started in four simple steps</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, idx) => (
              <div key={s.title} className="relative" data-testid={`step-${idx + 1}`}>
                <div className="card-nx h-full p-6">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#2563EB] text-sm font-bold text-white">
                      {idx + 1}
                    </span>
                    <s.icon className="h-5 w-5 text-[#B8860B]" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-[#0B192C] font-head">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-500">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          {stats && (
            <p className="mt-10 text-center text-xs text-slate-400" data-testid="landing-stats">
              Currently {stats.members} members · {stats.businesses} businesses · {stats.opportunities} opportunities · {stats.events} events
              <span className="ml-1 italic">(includes illustrative demo data)</span>
            </p>
          )}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="section-nx">
        <div className="container-nx">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#2563EB] to-[#3B82F6] px-6 py-16 text-center shadow-lg sm:py-20">
            <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-[#D4AF37]/20 blur-2xl" />
            <h2 className="relative text-3xl font-bold text-white font-head sm:text-4xl">
              Your Next Business Connection<br className="hidden sm:block" /> Starts Here.
            </h2>
            <div className="relative mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to={authed ? "/dashboard" : "/register"} className="btn-gold" data-testid="cta-join-btn">
                Join Vishwakarma Nexus
              </Link>
              <Link to="/businesses" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/10 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/20" data-testid="cta-explore-btn">
                Explore Businesses
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
